import {forwardRef} from 'react'
import {LoadingSpinner as DsLoadingSpinner} from 'pepsico-ds'

// Wrapper component for the LoadingSpinner from pepsico-ds
type LoadingSpinnerProps = {
  className?: string
  size?: number | string | {width: string; height: string}
  minHeight?: string
  centered?: boolean // Whether to center the spinner in its container
  overlay?: boolean // Whether to show as a full-screen overlay that blocks interactions
  inline?: boolean // Whether to render without the default block-height wrapper
  [key: string]: any // Allow any other prop for forwarding
}

const LoadingSpinner = forwardRef<any, LoadingSpinnerProps>(
  ({className = '', size = 25, minHeight, centered = true, overlay = false, inline = false, ...rest}, ref) => {
    let spinnerSize: {height: string; width: string}
    if (typeof size === 'object' && size && 'width' in size && 'height' in size) {
      spinnerSize = {height: size.height, width: size.width}
    } else {
      const pxSize = typeof size === 'number' ? `${size}px` : size || '25px'
      spinnerSize = {height: pxSize, width: pxSize}
    }

    // Create custom styles for minHeight and centering if provided
    const customStyles: React.CSSProperties = {}

    if (minHeight) {
      customStyles.minHeight = minHeight
    }

    // Only add centering styles if explicitly requested with minHeight
    if (centered && minHeight) {
      customStyles.display = 'flex'
      customStyles.justifyContent = 'center'
      customStyles.alignItems = 'center'
      customStyles.width = '100%'
    }

    // Omit props that shouldn't be passed to DOM elements
    const {size: _omit, overlay: _omitOverlay, ...restProps} = rest as any

    // If overlay mode, render as full-screen blocking overlay
    if (overlay) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-transparent">
          <DsLoadingSpinner
            ref={ref}
            size={spinnerSize}
            className={`select-none ${className}`}
            data-testid="loader-container"
            {...restProps}
          />
        </div>
      )
    }

    if (inline) {
      return (
        <DsLoadingSpinner
          ref={ref}
          size={spinnerSize}
          className={`select-none ${className}`}
          style={Object.keys(customStyles).length > 0 ? customStyles : undefined}
          data-testid="loader-container"
          {...restProps}
        />
      )
    }

    // Always wrap in a centered container by default
    return (
      <div className="flex h-32 w-full items-center justify-center">
        <DsLoadingSpinner
          ref={ref}
          size={spinnerSize}
          className={`select-none ${className}`}
          style={Object.keys(customStyles).length > 0 ? customStyles : undefined}
          data-testid="loader-container"
          {...restProps}
        />
      </div>
    )
  },
)

LoadingSpinner.displayName = 'LoadingSpinner'

export default LoadingSpinner
